import React, {useEffect, useState} from "react";
import {fetchCustomerHistory} from "../../Api/Api";
import {useWorkshop} from "../Common/WorkshopContext";
import {useParams} from "react-router-dom";
import {FaCalendarAlt, FaCar, FaCheckCircle, FaCogs, FaTools, FaUser} from "react-icons/fa";
import BillingModal from "../Billing/BillingModal";
import {useNotification} from "../Notification/NotificationContext";

const VisitHistory = () => {
    const [visitHistory, setVisitHistory] = useState([]);
    const { workshopId } = useWorkshop();
    const { customerId } = useParams();
    const [selectedJobCard, setSelectedJobCard] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const { showNotification } = useNotification();

    useEffect(() => {
        fetchHistory();
    }, [customerId, workshopId]);

    const fetchHistory = () => {
        fetchCustomerHistory(customerId)
            .then((res) => setVisitHistory(res.data))
            .catch((error) => showNotification({message:error, type: "error"}));
    }

        const handleBillPendingClick = (jobCard) => {
            setSelectedJobCard(jobCard);
            setShowModal(true);
        };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedJobCard(null);
        fetchHistory();
    };

    const billStatus = (visit) => {
        return visit?.jobCard?.billStatus !== "PENDING"
            || visit?.jobCard?.status !== "CANCELLED"
            || visit?.jobCard?.billStatus !== "BILLED"
    }

    return (
        <div className="max-w-8xl mx-auto my-10 p-8 bg-white shadow-lg rounded-lg">
            {/* Customer Details */}
            <div className="bg-blue-500 text-white p-6 rounded-lg shadow mb-8">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                    <FaUser /> Customer Details
                </h2>
                <div className="space-y-3 mt-4">
                    <p><strong>Name:</strong> {visitHistory?.customerName}</p>
                    <p><strong>Email:</strong> {visitHistory?.customerEmail}</p>
                    <p><strong>Phone:</strong> {visitHistory?.customerPhone}</p>
                </div>
            </div>

            {/* Timeline */}
            <div className="relative border-l-4 border-blue-500 mt-10 pl-5">
                {visitHistory?.visits?.length > 0 ? (
                    visitHistory.visits.map((visit, index) => (
                        <div key={visit.id} className="mb-12 relative">
                            {/* Timeline Dot */}
                            <div className="absolute left-[-16px] top-0 w-6 h-6 bg-blue-500 rounded-full border-white border-4"></div>

                            <div className="bg-gray-100 p-6 rounded-lg shadow-md">
                                <h3 className="text-xl font-semibold flex items-center gap-2">
                                    <FaCar className="text-blue-600" /> Visit {index + 1}
                                </h3>
                                <div className="mt-4 text-sm text-gray-700">
                                    <p className="flex items-center gap-2">
                                        <FaCalendarAlt /> {new Date(visit?.visitDate).toLocaleDateString()}
                                    </p>
                                    <p className="flex items-center gap-2">
                                        <FaCogs /> <strong>Status:</strong>
                                        <span className={`px-4 py-2 rounded-full text-white text-xs font-semibold 
                                            ${visit?.jobCard?.status === "COMPLETED" ? "bg-green-500" : "bg-yellow-500"}`}>
                                            {visit?.jobCard?.status}
                                        </span>
                                    </p>
                                    <p className="flex items-center gap-2">
                                        <FaCar /> <strong>Vehicle:</strong> {visit?.jobCard?.vehicleNumber}
                                    </p>
                                </div>

                                {/* Work Types */}
                                <div className="mt-6">
                                    <h4 className="text-lg font-semibold flex items-center gap-2">
                                        <FaTools className="text-blue-600" /> Work Types
                                    </h4>
                                    <ul className="list-disc ml-6 space-y-2 mt-3">
                                        {visit?.jobCard?.workTypes?.map((workType, idx) => (
                                            <li key={idx} className="text-sm">{workType}</li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Spare Parts */}
                                <div className="mt-6">
                                    <h4 className="text-lg font-semibold flex items-center gap-2">
                                        <FaCogs className="text-blue-600" /> Spare Parts
                                    </h4>
                                    <div className="overflow-x-auto mt-3">
                                        <table className="w-full text-sm border-collapse">
                                            <thead>
                                            <tr className="bg-blue-500 text-white">
                                                <th className="p-4">ID</th>
                                                <th className="p-4">Name</th>
                                                <th className="p-4">Quantity</th>
                                                <th className="p-4">Warranty</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {visit?.jobCard?.inventoryItems?.map((item, idx) => (
                                                <tr key={idx} className="border-b hover:bg-gray-200">
                                                    <td className="p-4">{item.id}</td>
                                                    <td className="p-4">{item.name}</td>
                                                    <td className="p-4">{item.quantity}</td>
                                                    <td className="p-4">
                                                        {item.warranty ?
                                                            <span className="bg-green-500 text-white px-3 py-1 rounded">Yes</span> :
                                                            <span className="bg-red-500 text-white px-3 py-1 rounded">No</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Workers */}
                                <div className="mt-6">
                                    <h4 className="text-lg font-semibold flex items-center gap-2">
                                        <FaUser className="text-blue-600" /> Workers
                                    </h4>
                                    <ul className="list-disc ml-6 space-y-2 mt-3">
                                        {visit?.jobCard?.employees?.map((em, idx) => (
                                            <li key={idx} className="text-sm">{em}</li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Bill Button */}
                                { (visit?.jobCard?.billStatus !== "PENDING" && visit?.jobCard?.status ==="COMPLETED")
                                     && (
                                    <div className="mt-6 text-right">
                                        <button
                                            className="bg-green-500 text-white px-6 py-3 rounded-lg shadow hover:bg-green-600 transition"
                                            onClick={() => handleBillPendingClick(visit?.jobCard)}
                                        >
                                            <FaCheckCircle className="inline-block mr-2" /> Generate Bill
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <p className="text-center text-gray-500">No visits found for this customer in this workshop.</p>
                )}
            </div>

            {/* Billing Modal */}
            {selectedJobCard && (
                <BillingModal jobCard={selectedJobCard} show={showModal} handleClose={handleCloseModal} />
            )}
        </div>
    );
};

export default VisitHistory;
